import { useQueryClient } from '@tanstack/react-query';
import { ArrowLeft } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import * as api from '../api';
import { EmployeeForm } from '../components/EmployeeForm';
import { employeeKeys, useCreateEmployee, useEmployee, useUpdateEmployee } from '../queries';
import { employeeName, type EmployeeInput } from '../types';

export function EmployeeFormPage() {
  const { publicId = '' } = useParams();
  const isEdit = Boolean(publicId);
  const navigate = useNavigate();

  const employeeQuery = useEmployee(isEdit ? publicId : -1);
  const create = useCreateEmployee();
  const update = useUpdateEmployee(publicId);
  const qc = useQueryClient();

  async function handleSubmit(values: EmployeeInput, photo: File | null) {
    const saved = isEdit ? await update.mutateAsync(values) : await create.mutateAsync(values);
    // Photo rides a separate multipart PATCH so the main save stays JSON.
    if (photo) {
      const withPhoto = await api.uploadEmployeePhoto(saved.public_id, photo);
      qc.setQueryData(employeeKeys.detail(saved.public_id), withPhoto);
      qc.invalidateQueries({ queryKey: employeeKeys.lists() });
    }
    navigate(`/hrm/employees/${saved.public_id}`);
  }

  if (isEdit && employeeQuery.isPending) {
    return (
      <div className="w-full max-w-3xl space-y-4">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-96 w-full" />
      </div>
    );
  }

  const employee = employeeQuery.data;

  return (
    <div className="w-full max-w-3xl space-y-8">
      <div className="flex items-center gap-3">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate(isEdit ? `/hrm/employees/${publicId}` : '/hrm/employees')}
        >
          <ArrowLeft />
        </Button>
        <h1 className="text-2xl font-semibold tracking-tight">
          {isEdit ? `Edit ${employee ? employeeName(employee) : 'employee'}` : 'New employee'}
        </h1>
      </div>

      <EmployeeForm
        submitLabel={isEdit ? 'Save changes' : 'Create employee'}
        currentPhoto={employee?.photo}
        currentName={employee ? employeeName(employee) : undefined}
        defaultValues={
          employee
            ? {
                user: employee.user ? String(employee.user) : '',
                first_name: employee.first_name,
                last_name: employee.last_name,
                email: employee.email,
                personal_email: employee.personal_email,
                phone_number: employee.phone_number,
                city: employee.city,
                address: employee.address,
                legal_name: employee.legal_name,
                legal_name_local: employee.legal_name_local,
                address_local: employee.address_local,
                contract_number: employee.contract_number,
                contract_date: employee.contract_date ?? '',
                employment_type: employee.employment_type,
                job_title: employee.job_title,
                department: employee.department ? String(employee.department) : '',
                manager: employee.manager ? String(employee.manager) : '',
                hire_date: employee.hire_date ?? '',
                trial_period_end: employee.trial_period_end ?? '',
                end_date: employee.end_date ?? '',
                status: employee.status,
                country: employee.country,
              }
            : undefined
        }
        onSubmit={handleSubmit}
        onCancel={() => navigate(isEdit ? `/hrm/employees/${publicId}` : '/hrm/employees')}
      />
    </div>
  );
}
